import { Injectable } from '@angular/core';
import { AmlService } from '@app/core/services/aml.service';
import { createEffect, ofType } from '@ngrx/effects';
import { from, of } from 'rxjs';
import { catchError, exhaustMap, map, mergeMap } from 'rxjs/operators';
import { TranslatableEffect } from '../../i18n/TranslatableEffects';
import { FirmService } from '../../services/firm.service';
import { AuthActions } from '../auth/auth.actions';
import { ToastActions } from '../toast/toast.actions';
import { FirmActions } from './firm.actions';

@Injectable()
export class FirmEffects extends TranslatableEffect {
  loadFirm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FirmActions.loadFirm),
      exhaustMap(() =>
        this.firmService.loadFirmType().pipe(
          map((firm) => FirmActions.loadFirmSucceeded({ firm })),
          catchError((error: unknown) => of(FirmActions.loadFirmFailed({ error }))),
        ),
      ),
    ),
  );

  saveFirm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FirmActions.saveFirm),
      exhaustMap(({ firm }) =>
        this.firmService.updateFirmType(firm).pipe(
          map((updatedFirm) => FirmActions.saveFirmSucceeded({ firm: updatedFirm })),
          catchError((error: unknown) => of(FirmActions.saveFirmFailed({ error }))),
        ),
      ),
    ),
  );

  saveFirmSuccessfulMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FirmActions.saveFirmSucceeded),
      map(() => ToastActions.showInfoMessage({ summary: this.translate('firmSettingsSaved'), detail: '' })),
    ),
  );

  saveFirmFailedMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FirmActions.saveFirmFailed),
      map(() =>
        ToastActions.showErrorMessage({
          summary: this.translate('somethingWentWrong'),
          detail: this.translate('pleaseTryAgainOrContactSupport'),
        }),
      ),
    ),
  );

  resetFirmAll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FirmActions.resetFirmAll),
      mergeMap(({ firm }) =>
        this.firmService.resetFirm(firm.id).pipe(
          mergeMap(() => [FirmActions.resetFirmSucceeded(), AuthActions.resetAuthStateAndStorage()]),
          catchError(() => of(FirmActions.resetFirmFailed())),
        ),
      ),
    ),
  );

  resetFirmTasks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FirmActions.resetFirmTasks),
      mergeMap(({ firm }) =>
        this.firmService.resetFirmTasks(firm.id).pipe(
          mergeMap(() => [FirmActions.resetFirmSucceeded(), AuthActions.resetAuthStateAndStorage()]),
          catchError(() => of(FirmActions.resetFirmFailed())),
        ),
      ),
    ),
  );

  activateSustainabilityReporting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FirmActions.activateSustainabilityReporting),
      exhaustMap(({ firmId, data }) =>
        this.firmService.activateSustainabilityReporting(firmId, data).pipe(
          mergeMap(() => [
            FirmActions.loadFirm(),
            FirmActions.activateSustainabilityReportingSucceeded(),
            ToastActions.showInfoMessage({ summary: this.translate('sustainabilityReportingActivated'), detail: '' }),
          ]),
          catchError((error: unknown) =>
            from([
              ToastActions.showErrorMessage({
                summary: this.translate('somethingWentWrong'),
                detail: this.translate('pleaseTryAgainOrContactSupport'),
                error,
                sticky: true,
              }),
              FirmActions.activateSustainabilityReportingFailed(),
            ]),
          ),
        ),
      ),
    ),
  );

  verifyAmlConnection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FirmActions.loadFirmSucceeded),
      exhaustMap(() =>
        this.amlService.getAmlApiEmail().pipe(
          map(({ email }) => FirmActions.verifyAmlConnectionSucceeded({ email })),
          catchError(() => of(FirmActions.verifyAmlConnectionFailed())),
        ),
      ),
    ),
  );

  loadFirmSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FirmActions.loadFirmSettings),
      mergeMap(() =>
        this.firmService.loadFirmSettings().pipe(
          mergeMap((settings) => [FirmActions.loadFirmSettingsSucceeded({ settings })]),
          catchError((error: unknown) =>
            of(
              FirmActions.loadFirmSettingsFailed({ error }),
              ToastActions.showErrorMessage({
                summary: this.translate('somethingWentWrong'),
                detail: this.translate('pleaseTryAgainOrContactSupport'),
                error,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  updateFirmSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FirmActions.saveFirmSettings),
      mergeMap(({ settings }) =>
        this.firmService.updateFirmSettings(settings).pipe(
          mergeMap(() => [
            FirmActions.saveFirmSettingsSucceeded({ settings }),
            ToastActions.showInfoMessage({ summary: this.translate('assignmentAgreementInfoSaved'), detail: '' }),
          ]),
          catchError((error: unknown) =>
            of(
              FirmActions.saveFirmSettingsFailed({ error }),
              ToastActions.showErrorMessage({
                summary: this.translate('somethingWentWrong'),
                detail: this.translate('pleaseTryAgainOrContactSupport'),
                error,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  resetFirmSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FirmActions.resetFirmSettings),
      mergeMap(() =>
        this.firmService.resetFirmSettings().pipe(
          mergeMap((settings) => [
            FirmActions.resetFirmSettingsSucceeded({ settings }),
            ToastActions.showInfoMessage({ summary: this.translate('resetFirmSettingMessage'), detail: '' }),
          ]),
          catchError((error: unknown) =>
            of(
              FirmActions.resetFirmSettingsFailed({ error }),
              ToastActions.showErrorMessage({
                summary: this.translate('somethingWentWrong'),
                detail: this.translate('pleaseTryAgainOrContactSupport'),
                error,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  constructor(
    private firmService: FirmService,
    private amlService: AmlService,
  ) {
    super('FirmEffects');
  }
}
